// Librairies
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect, inject, NgZone, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Extension, ViewerInitializedEvent, ViewerModule, ViewerOptions } from 'ng2-adsk-forge-viewer';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

// Services
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { ZoneService } from 'src/app/services/api/zones/zone.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';

// Interfaces
import { defaultModel, Model } from 'src/app/interfaces/api/model';
import { Project } from 'src/app/interfaces/api/project';
import { Track } from 'src/app/interfaces/api/track';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';

// Classes
import { Queue } from 'src/app/classes/shared/queue';
import * as threeDTools from 'src/app/classes/shared/threeDTools';
import { AppEvent } from 'src/app/classes/viewer/appEvent';
import { ElementInfoExtension } from 'src/app/classes/viewer/element-info/extension';
import { OriginExtension } from 'src/app/classes/viewer/origin/extension';
import { UnitShape } from 'src/app/classes/viewer/shapes/unit';
import { UnitExtension } from 'src/app/classes/viewer/units/extension';
import { ZoneExtension } from 'src/app/classes/viewer/zones/extension';

// Components
import { PlayerComponent } from 'src/app/components/shared/player/player.component';
import { ButtonComponent } from 'ui-library';

@Component({
    selector: 'app-viewer-forge',
    imports: [
        MatToolbarModule,
        TranslateModule,
        MatIconModule,
        CommonModule,
        PlayerComponent,
        ButtonComponent,
        ViewerModule
    ],
    standalone: true,
    templateUrl: './viewer-forge.component.html',
    styleUrl: './viewer-forge.component.scss'
})

export class ViewerForgeComponent implements OnInit, OnDestroy {
    // Services
    private readonly auth: AuthService = inject(AuthService);
    private readonly modelService: ModelService = inject(ModelService);
    private readonly zoneService: ZoneService = inject(ZoneService);
    private readonly unitService: UnitService = inject(UnitService);
    private readonly trackerService: TrackerService = inject(TrackerService);
    private readonly trackService: TrackService = inject(TrackService);
    private readonly socket: WebSocketService = inject(WebSocketService);
    private readonly appEvent: AppEvent = inject(AppEvent);
    private readonly projectService: ProjectService = inject(ProjectService);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly router: Router = inject(Router);
    private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly ngZone: NgZone = inject(NgZone);

    // Signals
    private readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
    private readonly currentUser: Signal<User> = this.auth.currentUser;
    private trackerSignal = signal<Tracker[]>([]);
    private rawUnitSignal = signal<UnitShape[]>([]);
    public unitSignal = signal<UnitShape[]>([]);

    // Variables
    public viewerOptions: ViewerOptions = {
        initializerOptions: {
            env: "",
            getAccessToken: () => { },
            api: "",
            enableMemoryManagement: false
        },
        onViewerInitialized: () => { }
    };

    public documentId: string = "";
    public viewer: any;

    originExt: any;
    zoneExt: any;
    unitExt: any;
    elInfoExt: any;

    model: Model = defaultModel;
    units: BehaviorSubject<Unit[]> = new BehaviorSubject<Unit[]>([]);
    project: any;
    socketTrackSub: any;
    socketUnitSub: any;
    playMode: string = 'realtime';
    viewerErrors: any[] = Config.viewerErrors;
    playerValueSbj: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    playerDataSbj: BehaviorSubject<any> = new BehaviorSubject<any>({});
    units$: BehaviorSubject<Unit[]> = new BehaviorSubject<Unit[]>([]);
    playerValue: Observable<any> = this.playerValueSbj.asObservable();
    error: string = "";

    showTimeline = false;
    liveState: boolean = true;
    playerState: string = "";
    queue: Queue<any> = new Queue();
    processing: boolean = false;
    destroy$ = new Subject<void>();
    replayTrackers: string[] = [];
    centered: boolean = false;
    trackers: Tracker[] = [];

    public showViewer = true;
    public resetting = false;
    private lastProjectId: string | null = null;
    private cachedToken: { access_token: string, expires_in: number } | null = null;

    constructor() {
        this.playerValueSbj = new BehaviorSubject<number>(0);
        this.playerDataSbj = new BehaviorSubject<any>({});
        this.playerValue = this.playerValueSbj.asObservable();

        effect(() => {
            const project = this.currentProject();

            if (!project) return;

            if (this.lastProjectId && project.id !== this.lastProjectId) {
                this.resetViewer();
            }
        })

    }

    ngOnInit() {
        this.modelService.getToken().pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.cachedToken = res;
            this.initializeComponent();
        })
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.cleanup();
    }

    private initializeComponent(): void {
        this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(res => {
            if (this.project && res.id == this.project.id) return;

            this.cleanup()

            this.project = res;

            this.trackerService.getTrackers().subscribe({
                next: (allTrackers: Tracker[]) => {
                    this.trackerSignal.set(allTrackers);
                },
                error: (err) => {
                    console.error('Error fetching trackers:', err);
                }
            });

            this.unitService.getUnits({ populate: true }).subscribe((units: Unit[]) => {
                this.units$.next(units);
            });

            this.modelService.getCurrentModel(this.project.id).pipe(takeUntil(this.destroy$)).subscribe(res => {
                this.model = res;

                if (this.model.urn == "_")
                    this.model.urn = undefined

                if (this.model.origin.position.lat && this.model.origin.position.lon) {
                    const latLonXY = threeDTools.getXYFromLatLonAlt2(this.model.origin.position.lat, this.model.origin.position.lon, 0);

                    this.model.origin.projectLatLonXY = [latLonXY[0] ?? 0, latLonXY[1] ?? 0];
                } else {
                    this.model.origin.projectLatLonXY = [0, 0]
                }

                if (this.model.origin.position.x != undefined && this.model.origin.position.y != undefined) {
                    this.model.origin.projectXY = [parseFloat(String(this.model.origin.position.x)), parseFloat(String(this.model.origin.position.y))];
                } else {
                    this.model.origin.projectXY = [0, 0]
                }

                this.model.offset = {
                    x: 0,
                    y: 0,
                    z: 0
                }

                this.viewerOptions = {
                    initializerOptions: {
                        env: "AutodeskProduction2",
                        getAccessToken: (onGetAccessToken: (token: string, expire: number) => void) => {
                            if (this.cachedToken) {
                                onGetAccessToken(
                                    this.cachedToken.access_token,
                                    this.cachedToken.expires_in
                                );
                            }
                        },
                        api: "streamingV2",
                        enableMemoryManagement: true
                    },
                    viewerConfig: {
                        extensions: ["Autodesk.DocumentBrowser",
                            OriginExtension.extensionName,
                            ZoneExtension.extensionName,
                            UnitExtension.extensionName,
                            //  ElementInfoExtension.extensionName
                        ],
                        theme: this.currentUser().theme == "light" ? "bim-theme" : "dark-theme"
                    },
                    onViewerScriptsLoaded: () => {
                        Extension.registerExtension(OriginExtension.extensionName, OriginExtension);
                        Extension.registerExtension(ZoneExtension.extensionName, ZoneExtension);
                        Extension.registerExtension(UnitExtension.extensionName, UnitExtension);
                        // Extension.registerExtension(ElementInfoExtension.extensionName, ElementInfoExtension);

                    },
                    onViewerInitialized: (args: ViewerInitializedEvent) => this.loadDocument(args)
                };
            });
        });
    }

    public loadDocument(args: ViewerInitializedEvent): void {
        try {

            this.viewer = args.viewer;

            this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, () => {
                this.onToolbarCreated();
            });

            args.viewerComponent.onError
                .pipe(takeUntil(this.destroy$))
                .subscribe(errorCode => {
                    this.model.urn = undefined;
                    this.error = this.viewerErrors[errorCode - 1] || 'Unknown viewer error';
                    console.error('Viewer error - errorCode:', errorCode);
                    this.cdr.detectChanges();
                });

            args.viewerComponent.DocumentId = 'urn:' + this.model.urn;
        } catch (error) {
            console.error('Error in loadDocument:', error);
            this.error = 'Failed to initialize viewer';
        }
    }

    private onToolbarCreated(): void {
        try {
            const offset = this.viewer.model?.getData()?.globalOffset;

            if (offset && offset.x < 10 ** 5) {
                this.model.offset = offset;
            }

            this.loadExtensionsWithRetry();
        } catch (error) {
            console.error('Error handling toolbar created:', error);
        }
    }

    private async loadExtensionsWithRetry(maxRetries: number = 3): Promise<void> {
        try {
            this.unitExt = this.viewer.loadedExtensions[UnitExtension.extensionName];
            this.originExt = this.viewer.loadedExtensions[OriginExtension.extensionName];
            this.zoneExt = this.viewer.loadedExtensions[ZoneExtension.extensionName];
            // this.elInfoExt = this.viewer.loadedExtensions[ElementInfoExtension.extensionName];


            if (this.unitExt && this.originExt && this.zoneExt) {

                this.configureExtensions();
                this.initializeExtensions();

                if (this.elInfoExt && typeof this.elInfoExt.onUnitsInitialized === 'function') {
                    this.elInfoExt.onUnitsInitialized(this.unitSignal());
                }

                this.configureViewer();
                this.initSocket();

                return;
            }

        } catch (error) {
            console.error(`Error loading extensions on attempt:`, error);
        }
    }

    private configureExtensions(): void {
        try {
            if (!this.translate) {
                console.error('ViewerForgeComponent: translate service is not available!');
                return;
            }

            if (this.unitExt) {
                this.unitExt.units$ = this.units$;
                this.unitExt.unitService = this.unitService;
                this.unitExt.modelService = this.modelService;
                this.unitExt.trackerService = this.trackerService;
                this.unitExt.trackService = this.trackService;
                this.unitExt.translate = this.translate;
                this.unitExt.model = this.model;
                this.unitExt.playerData = this.playerDataSbj;

                // Signals
                this.unitExt.unitSignal = this.unitSignal;
            }

            if (this.originExt) {
                this.originExt.modelService = this.modelService;
                this.originExt.translate = this.translate;
                this.originExt.model = this.model;
                if (this.unitExt) this.originExt.unitExt = this.unitExt;
                if (this.zoneExt) this.originExt.zoneExt = this.zoneExt;
            }

            if (this.zoneExt) {
                this.zoneExt.zoneService = this.zoneService;
                this.zoneExt.translate = this.translate;
                this.zoneExt.model = this.model;
            }

            if (this.elInfoExt) {
                this.elInfoExt.modelService = this.modelService;
                this.elInfoExt.trackerService = this.trackerService;
                this.elInfoExt.translate = this.translate;
                this.elInfoExt.model = this.model;
                this.elInfoExt.units$ = this.units$;
                this.elInfoExt.appEvent = this.appEvent;
            }

            if (this.unitExt && this.elInfoExt) {
                this.unitExt.elementInfoExt = this.elInfoExt;
            }

        } catch (error) {
            console.error('Error configuring extensions:', error);
        }
    }

    private initializeExtensions(): void {
        try {
            if (this.originExt?.init) this.originExt.init();
            if (this.unitExt?.init) this.unitExt.init();
            if (this.zoneExt?.init) this.zoneExt.init();
            if (this.elInfoExt?.init) this.elInfoExt.init();

        } catch (error) {
            console.error('Error initializing extensions:', error);
        }
    }

    private configureViewer(): void {
        try {
            this.viewer.setProgressiveRendering(false);
            this.viewer.navigation.toPerspective();

            const up = this.model.origin.up === "z"
                ? new THREE.Vector3(0, 0, 1)
                : new THREE.Vector3(0, 1, 0);

            this.viewer.navigation.setWorldUpVector(up, true);
        } catch (error) {
            console.error('Error configuring viewer:', error);
        }
    }

    initSocket(): void {
        try {
            this.socketTrackSub = this.socket.getUpdate("Track")
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (data: Track[]) => {

                        this.ngZone.run(() => {
                            this.handleTrackUpdate(data);
                        });
                    },
                    error: (err) => {
                        console.error('Socket Track error:', err);
                    }
                });
        } catch (error) {
            console.error('Error initializing sockets:', error);
        }
    }

    private handleTrackUpdate(data: Track[]): void {
        try {
            if (data.length === 0 || !this.liveState || !this.unitExt) {
                return;
            }

            for (const track of data) {
                if (!track?.tracker) continue;

                const trackerId = typeof track.tracker === 'string'
                    ? track.tracker
                    : track.tracker?.id;

                if (trackerId && !this.queue.findBy("trackerId", trackerId)) {
                    this.queue.enqueue({ trackerId, track });
                }
            }

            if (!this.processing) {
                this.processQueue();
            }
        } catch (error) {
            console.error('Error handling track update:', error);
        }
    }

    private handleUnitUpdate(data: UnitShape[]): void {
        if (!this.liveState || this.playerState !== "play" || !this.unitExt) {
            return;
        }

        try {
            data.forEach(unit => {
                if (this.unitExt?.updateUnit) {
                    this.unitExt.updateUnit(unit);
                }
            });

            if (this.viewer?.impl && data.length > 0) {
                this.viewer.impl.sceneUpdated(true);
            }
        } catch (error) {
            console.error('Error handling unit update:', error);
        }
    }

    cleanup(): void {
        try {
            if (this.socketTrackSub) {
                this.socketTrackSub.unsubscribe();
                this.socketTrackSub = null;
            }

            if (this.socketUnitSub) {
                this.socketUnitSub.unsubscribe();
                this.socketUnitSub = null;
            }

            if (this.viewer) {
                try {
                    if (this.unitExt) this.viewer.unloadExtension(UnitExtension.extensionName);
                    if (this.zoneExt) this.viewer.unloadExtension(ZoneExtension.extensionName);
                    if (this.originExt) this.viewer.unloadExtension(OriginExtension.extensionName);
                    if (this.elInfoExt) this.viewer.unloadExtension(ElementInfoExtension.extensionName);

                    this.viewer.finish();
                    Autodesk.Viewing.shutdown();
                } catch (error) {
                    console.error('Error during viewer cleanup:', error);
                }

                this.viewer = null;
            }

            this.rawUnitSignal.set([]);
            this.trackerSignal.set([]);

            this.queue = new Queue();
            this.processing = false;

            this.unitExt = null;
            this.originExt = null;
            this.zoneExt = null;
            this.elInfoExt = null;
        } catch (error) {
            console.error('Error during cleanup:', error);
        }
    }

    async processQueue(): Promise<void> {
        if (this.processing || !this.unitExt) return;

        this.processing = true;

        try {
            while (!this.queue.isEmpty()) {
                const next = this.queue.next();
                this.queue.dequeue();
                await this.processQueueItem(next);
            }
        } catch (error) {
            console.error('Error processing queue:', error);
        } finally {
            this.processing = false;
        }
    }

    private async processQueueItem(item: any): Promise<void> {
        try {
            const currentUnits = this.unitSignal();

            const currentUnit = currentUnits.find(u =>
                u.trackers.map(t => t.id).includes(item.trackerId)
            );

            if (currentUnit && this.isCraneUnit(currentUnit)) {
                await this.handleCraneUpdate(currentUnit, item.track);
            }

            if (this.unitExt?.updateFromTrack) {
                this.unitExt.updateFromTrack(item.track);
            }

            if (this.viewer?.impl) {
                this.viewer.impl.sceneUpdated(true);
            }
        } catch (error) {
            console.error('Error processing queue item:', error);
        }
    }

    private isCraneUnit(unit: UnitShape): boolean {
        return unit.collider?.geometry === "Crane hook" || unit.collider?.geometry === "Crane";
    }

    private async handleCraneUpdate(currentUnit: UnitShape, track: Track): Promise<void> {
        try {
            currentUnit.lastPosition.z = track.z;
            const unitGeometry = currentUnit.collider.geometry;

            const currentUnits = this.unitSignal();

            const hookUnit = unitGeometry === "Crane hook"
                ? currentUnit
                : currentUnits.find(u =>
                    u.collider.geometry === "Crane hook" &&
                    u.trackers.map(t => t.id).includes(currentUnit.crane.hookTracker)
                );

            const craneUnit = unitGeometry === "Crane"
                ? currentUnit
                : currentUnits.find(u =>
                    u.collider.geometry === "Crane" &&
                    currentUnit.trackers.map(t => t.id).includes(u.crane.hookTracker)
                );

            if (craneUnit && hookUnit) {
                hookUnit.collider.height = (
                    craneUnit.lastPosition.z +
                    (craneUnit.crane.trackerOnTop ? 0 : craneUnit.crane.height)
                ) - hookUnit.lastPosition.z + hookUnit.collider.hookMargin;

                if (this.unitExt?.updateUnit) {
                    this.unitExt.updateUnit(hookUnit);
                }
            }
        } catch (error) {
            console.error('Error handling crane update:', error);
        }
    }

    public documentChanged(event: any): void {
        try {
            const { document } = event;
            if (!document?.getRoot()) return;

        } catch (error) {
            console.error('Error handling document change:', error);
        }
    }

    onPlayerChanged(data: any): void {
        try {
            this.playMode = data.playMode;
            this.showTimeline = data.showTimeline;
            this.liveState = data.playMode == "replay" ? false : data.liveState;
            this.playerState = data.playerState;
        } catch (error) {
            console.error('Error handling player change:', error);
        }
    }

    onTrackerPositionsChanged(tracks: Track[]): void {
        try {

            if (!tracks || tracks.length === 0 || !this.unitExt) return;


            const replayTracks = this.replayTrackers.length
                ? tracks.filter((t: Track) =>
                    this.replayTrackers.includes(
                        typeof t.tracker === 'string' ? t.tracker : t.tracker?.id || ''
                    )
                )
                : tracks;

            const currentUnits: UnitShape[] = this.unitExt.units;

            replayTracks.forEach(async (track: Track) => {
                try {
                    const currentUnit = currentUnits.find(u =>
                        u.trackers.map(tracker =>
                            typeof tracker === 'string' ? tracker : tracker.id
                        ).includes(
                            typeof track.tracker === 'string' ? track.tracker : track.tracker.id
                        )
                    );

                    if (currentUnit && this.isCraneUnit(currentUnit)) {
                        await this.handleCraneUpdate(currentUnit, track);
                    }

                    if (this.unitExt?.updateFromTrack) {
                        this.unitExt.updateFromTrack(track);
                    }
                } catch (error) {
                    console.error('Error processing individual track:', error);
                }
            });
        } catch (error) {
            console.error('Error handling tracker positions changed:', error);
        }
    }

    goToSettings(): void {
        try {
            this.router.navigate(['project/project-settings/models']);
        } catch (error) {
            console.error('Error navigating to settings:', error);
        }
    }

    resetViewer(): void {
        window.location.reload();
    }
}